import axios from "axios";
import RvApi from "@/rvapi";
import { defineStore } from "pinia";
import constant from "@/constant";
import type {
  PriceManagementProductState,
  PriceManagementUpdatePriceListRequest,
  PriceManagementUpdatePriceListResponse,
  PriceManagementCloneResponse,
  PriceManagementDeleteResponse,
  PriceManagementUpdateVatRequest,
  PriceManagementUpdateVatResponse,
  PriceManagementUpdateSapPriceRequest,
  PriceManagementUpdateSapPriceResponse,
} from "@/types/priceManagement";

const { PRICE_MANAGEMENT } = constant;

// Backend errorCode values mapped to their user-facing message
const CLONE_ERROR_CODE_MESSAGES: Record<string, string> = {
  PRICE_LIST_ID_REQUIRED: PRICE_MANAGEMENT.CLONE_ID_REQUIRED_ERROR_MESSAGE,
  PRICE_LIST_NOT_FOUND: PRICE_MANAGEMENT.CLONE_NOT_FOUND_ERROR_MESSAGE,
  DUPLICATE_PRODUCT_MAPPING:
    PRICE_MANAGEMENT.CLONE_DUPLICATE_MAPPING_ERROR_MESSAGE,
};

const DELETE_ERROR_CODE_MESSAGES: Record<string, string> = {
  ERR_PRICE_LIST_NOT_FOUND: PRICE_MANAGEMENT.DELETE_NOT_FOUND_ERROR_MESSAGE,
  ERR_STATUS_DRAFT_NOT_ALLOWED:
    PRICE_MANAGEMENT.DELETE_STATUS_NOT_ALLOWED_ERROR_MESSAGE,
  ERR_RELATED_RECORDS_EXIST:
    PRICE_MANAGEMENT.DELETE_RELATED_RECORDS_ERROR_MESSAGE,
};

function getErrorCode(e: unknown): string | undefined {
  return axios.isAxiosError(e) ? e.response?.data?.errorCode : undefined;
}

export const usePriceManagementStore = defineStore("priceManagement", {
  state: (): PriceManagementProductState => ({
    priceLists: [],
    isLoading: false,
    error: null,
  }),
  actions: {
    async getPriceLists() {
      this.isLoading = true;
      this.error = null;
      try {
        this.priceLists =
          (await RvApi.getPriceLists(PRICE_MANAGEMENT.PRODUCT_CODE)) ?? [];
      } catch {
        this.error = "Failed to fetch orders. Please try again.";
        this.priceLists = [];
      } finally {
        this.isLoading = false;
      }
    },

    async updatePriceList(payload: PriceManagementUpdatePriceListRequest) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.updatePriceList(payload);
        if (response.status === "error") {
          return response;
        }
        if (response.status === "success") {
          const priceListIndex = this.priceLists.findIndex(
            (priceList) => priceList.priceListId === payload.priceListId,
          );
          if (priceListIndex >= 0) {
            const existingPriceList = this.priceLists[priceListIndex];
            const updatedPriceList = {
              ...existingPriceList,
              isActive: payload.isActive,
              priceList: {
                ...existingPriceList.priceList,
                id: payload.priceListId,
                priceListName: payload.priceListName,
                priceListType: payload.priceListType,
                currencyCode: payload.currencyCode,
                effectiveFrom: payload.effectiveFrom,
                effectiveTo: payload.effectiveTo,
                status: payload.status,
              },
            };
            this.priceLists.splice(priceListIndex, 1, updatedPriceList);
          }
        }
        return response;
      } catch {
        return {
          status: "error",
          message: PRICE_MANAGEMENT.UPDATE_ERROR_MESSAGE,
        } satisfies PriceManagementUpdatePriceListResponse;
      } finally {
        this.isLoading = false;
      }
    },

    setError(errorMessage: string) {
      this.error = errorMessage;
    },

    clearError() {
      this.error = null;
    },

    async clonePriceList(priceListId: string) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.clonePriceList(priceListId);
        if (response.status === "success") {
          await this.getPriceLists();
        }
        return response;
      } catch (e) {
        return {
          status: "error",
          message:
            CLONE_ERROR_CODE_MESSAGES[getErrorCode(e) ?? ""] ??
            PRICE_MANAGEMENT.CLONE_ERROR_MESSAGE,
        } satisfies PriceManagementCloneResponse;
      } finally {
        this.isLoading = false;
      }
    },

    async deletePriceList(priceListId: string) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.deletePriceList(priceListId);
        if (response.status === "success") {
          await this.getPriceLists();
        }
        return response;
      } catch (e) {
        return {
          status: "error",
          message:
            DELETE_ERROR_CODE_MESSAGES[getErrorCode(e) ?? ""] ??
            PRICE_MANAGEMENT.DELETE_ERROR_MESSAGE,
        } satisfies PriceManagementDeleteResponse;
      } finally {
        this.isLoading = false;
      }
    },

    async updateVatPrice(payload: PriceManagementUpdateVatRequest) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.updateVatPrice(payload);
        if (response.status === "success") {
          await this.getPriceLists();
        }
        return response;
      } catch {
        return {
          status: "error",
          message: PRICE_MANAGEMENT.VAT_UPDATE_ERROR_MESSAGE,
        } satisfies PriceManagementUpdateVatResponse;
      } finally {
        this.isLoading = false;
      }
    },

    async updateSapPrice(payload: PriceManagementUpdateSapPriceRequest) {
      this.isLoading = true;
      this.error = null;
      try {
        const response = await RvApi.updateSapPrice(payload);
        if (response.status === "success") {
          await this.getPriceLists();
        }
        return response;
      } catch {
        return {
          status: "error",
          message: PRICE_MANAGEMENT.SAP_UPDATE_ERROR_MESSAGE,
        } satisfies PriceManagementUpdateSapPriceResponse;
      } finally {
        this.isLoading = false;
      }
    },
  },
});
